import {
  Alignment,
  AnchorButton,
  Button,
  Menu,
  MenuItem,
  Navbar,
  Popover,
  PopoverInteractionKind,
  PopoverPosition,
  Tooltip,
} from "@blueprintjs/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext } from "react";
import { Link, NavLink, useHistory } from "react-router-dom";
import { useAuth } from "../screens/auth/auth";
import UserPhoto from "./UserPhoto";
import {
  faPencilAlt,
  faDatabase,
  faCog,
  faBook,
  faSignInAlt,
  faTags,
  faSortDown,
} from "@fortawesome/pro-solid-svg-icons";
import { OrgContext, UserContext } from "../contexts";
import Feature from "../feature";
import { BrandIcon, NavbarLogo } from "./NavbarLogo";
import { useIsDesktop } from "@hooks";
import styled from "styled-components";
import { CommunityTierTag, TeamTierTag } from "./TierTag";
import AppTourMarker from "./AppTour";
import { slugifyOrgName } from "../screens/dashboard/utils";
import { hideAppTour } from "../util";

const SignInLink = styled.a`
  color: var(--global-dark-gray);
`;

interface Props {
  minimal?: boolean;
  showLogin?: boolean;
}

const TitleLink: React.FC<React.PropsWithChildren<{ to: string }>> = (
  props
) => (
  <NavLink
    role="button"
    className="bp3-button bp3-minimal"
    activeClassName="bp3-intent-primary"
    {...props}
  />
);

const AppNavbar: React.FC<Props> = ({ minimal = false, showLogin = true }) => {
  const { user } = useContext(UserContext);
  const { org, setOrg } = useContext(OrgContext);
  const { install, logout } = useAuth();
  const hideTour = hideAppTour(org, user);
  const isDesktop = useIsDesktop();
  const history = useHistory();
  const registryPages = ["/explore", "/r"];
  // TODO update when we get rid of the old playground
  // we will no longer need "/s" because snippets will be at "/playground/s"
  const editorPages = ["/playground", "/s"];

  const orgLandingPage = org?.name
    ? `/orgs/${slugifyOrgName(org?.name || "-")}/`
    : null;

  // If we are logged in, the app landing page will be dashboard.
  // If not, we will go to the new app landing page information
  const appNavBarPage = orgLandingPage || `/products/semgrep-app`;

  return (
    <div className={minimal ? "minimal" : "normal"}>
      {/* height: "auto" will expand the navbar vertically when its contents wrap on mobile  */}
      <Navbar style={{ height: "auto" }} fixedToTop={!minimal}>
        <Navbar.Group align={Alignment.LEFT}>
          {!minimal && (
            <Link to={orgLandingPage || `/`}>
              {isDesktop ? <NavbarLogo /> : <BrandIcon />}
            </Link>
          )}
          <NavLink
            role="button"
            className="bp3-button bp3-minimal"
            activeClassName="bp3-intent-primary"
            to="/r"
            isActive={() => {
              return (
                registryPages.indexOf(window.location.pathname) > -1 ||
                window.location.pathname.includes("/p/")
              );
            }}
          >
            <span className="bp3-button-text">
              {isDesktop ? "Registry" : <FontAwesomeIcon icon={faDatabase} />}
            </span>
          </NavLink>
          <NavLink
            role="button"
            className="bp3-button bp3-minimal"
            activeClassName="bp3-intent-primary"
            to="/playground"
            isActive={() => {
              return (
                editorPages.indexOf(window.location.pathname) > -1 ||
                window.location.pathname.includes("/s/")
              );
            }}
          >
            <span className="bp3-button-text">
              {isDesktop ? (
                "Playground"
              ) : (
                <FontAwesomeIcon icon={faPencilAlt} />
              )}
            </span>
          </NavLink>
          <NavLink
            role="button"
            className="bp3-button bp3-minimal"
            activeClassName="bp3-intent-primary"
            to={appNavBarPage}
            isActive={() => {
              return window.location.pathname === appNavBarPage;
            }}
          >
            <span>{isDesktop ? "App" : <FontAwesomeIcon icon={faCog} />}</span>
          </NavLink>
          <NavLink
            role="button"
            className="bp3-button bp3-minimal"
            activeClassName="bp3-intent-primary"
            to="/pricing"
            isActive={() => {
              return window.location.pathname === "/pricing";
            }}
          >
            <span className="bp3-button-text">
              {isDesktop ? "Pricing" : <FontAwesomeIcon icon={faTags} />}
            </span>
          </NavLink>
          <Popover
            minimal
            boundary="viewport"
            interactionKind={PopoverInteractionKind.HOVER}
            hoverCloseDelay={200}
            hoverOpenDelay={200}
            content={
              <Menu className="help-details-dropdown">
                <MenuItem
                  text={
                    <span>
                      {isDesktop ? "Docs" : <FontAwesomeIcon icon={faBook} />}
                    </span>
                  }
                  href="/docs/"
                ></MenuItem>
                {isDesktop && (
                  <MenuItem
                    text="Tutorial"
                    onClick={() => {
                      history.push(`/learn/`);
                    }}
                  />
                )}
              </Menu>
            }
            target={
              <Navbar.Heading>
                <AnchorButton href="/docs/" minimal>
                  <span className="bp3-button-text">
                    {isDesktop ? "Docs" : <FontAwesomeIcon icon={faBook} />}
                  </span>
                </AnchorButton>
              </Navbar.Heading>
            }
          />
          {user?.roles?.admin === true ? (
            <TitleLink to={"/admin"}>
              <span className="bp3-button-text">Admin</span>
            </TitleLink>
          ) : null}
        </Navbar.Group>
        <Navbar.Group align={Alignment.RIGHT}>
          {user !== undefined ? (
            <>
              {hideTour || <AppTourMarker user={user} />}
              {org?.name && isDesktop ? (
                Feature.hasTeamsTier({ user, org }) ? (
                  <TeamTierTag orgName={org.name} />
                ) : (
                  <CommunityTierTag orgName={org.name} />
                )
              ) : null}
              <Popover
                minimal
                boundary="viewport"
                content={
                  <Menu className="user-details-dropdown">
                    {user.orgs.map((availableOrg) => {
                      if (availableOrg.id === org?.id) return null;
                      return (
                        <MenuItem
                          text={
                            <span>
                              Switch to <strong>{availableOrg.name}</strong>
                            </span>
                          }
                          key={availableOrg.id}
                          onClick={() => {
                            setOrg(availableOrg);
                          }}
                        />
                      );
                    })}
                    <MenuItem
                      text="Add organization"
                      onClick={async () => {
                        await install(user.auth_provider_type);
                      }}
                    />
                  </Menu>
                }
                target={
                  <Navbar.Heading>
                    <Button style={{ fontSize: "16px" }} minimal>
                      {org?.name ?? "(add organization)"}{" "}
                      <FontAwesomeIcon
                        icon={faSortDown}
                        color="var(--global-mid-gray)"
                        pull="right"
                        size="sm"
                      />
                    </Button>
                  </Navbar.Heading>
                }
              />
              <Navbar.Divider />
              <Popover
                minimal
                boundary="viewport"
                position="bottom"
                content={
                  <Menu className="user-details-dropdown">
                    <MenuItem
                      icon="log-out"
                      text="sign out"
                      onClick={async () => {
                        await logout();
                      }}
                    />
                  </Menu>
                }
                target={
                  <Navbar.Heading>
                    <UserPhoto profile_photo={user.profile_photo} />
                  </Navbar.Heading>
                }
              />
            </>
          ) : showLogin ? (
            <Tooltip
              content={
                "Sign in to save custom rules and manage Semgrep policies in CI!"
              }
              position={PopoverPosition.BOTTOM}
            >
              <SignInLink
                href="/login"
                style={{ color: "--global-purple-highlight" }}
              >
                {isDesktop ? (
                  "Sign in / Sign up free"
                ) : (
                  <FontAwesomeIcon icon={faSignInAlt} />
                )}
              </SignInLink>
            </Tooltip>
          ) : undefined}
        </Navbar.Group>
      </Navbar>
    </div>
  );
};

export default AppNavbar;
